import { BaseEntity } from "@memberjunction/core";
import { RegisterClass } from "@memberjunction/global";
import { MJAPIKeyEntity } from "@memberjunction/core-entities";

/**
 * Extended MJAPIKeyEntity with client-safe utilities.
 *
 * Key Generation Flow:
 * 1. Client generates a random API key using GenerateAPIKey()
 * 2. Client stores the raw key in the Hash field temporarily
 * 3. Client saves the entity via GraphQL
 * 4. Server-side hook intercepts the save, hashes the raw key, stores the hash
 * 5. The raw key is only known to the client at generation time
 *
 * NOTE: Actual hashing is done server-side in IzzyLib's APIKeyEntityExtended
 * to avoid bundling Node.js crypto in browser builds.
 */
@RegisterClass(BaseEntity, 'MJ: API Keys')
export class APIKeyEntityExtended extends MJAPIKeyEntity {

    /**
     * Generates a new API key string (client-safe, no crypto dependency).
     * Uses Math.random which is sufficient for key generation when combined
     * with server-side hashing.
     *
     * @param prefix Optional prefix for the key (default: "izzy_sk_")
     * @param length Number of random characters (default: 50)
     * @returns The generated API key string
     *
     * @example
     * ```typescript
     * const apiKeyEntity = await md.GetEntityObject<APIKeyEntityExtended>('API Keys');
     * const rawKey = APIKeyEntityExtended.GenerateAPIKey();
     *
     * // Store raw key in Hash field - server will hash it on save
     * apiKeyEntity.Hash = rawKey;
     * apiKeyEntity.OrganizationID = orgId;
     * apiKeyEntity.Label = 'Production API Key';
     * await apiKeyEntity.Save();
     *
     * // Show rawKey to user ONCE - it cannot be retrieved later
     * console.log('Your API key (save this, it will not be shown again):', rawKey);
     * ```
     */
    public static GenerateAPIKey(prefix: string = 'izzy_sk_', length: number = 50): string {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
        let result = prefix;

        for (let i = 0; i < length; i++) {
            result += chars.charAt(Math.floor(Math.random() * chars.length));
        }

        return result;
    }

    /**
     * Checks if this API key is currently valid for use.
     * Validates status and expiration.
     *
     * @returns true if the key is active and not expired
     */
    public IsValid(): boolean {
        // Check status
        if (this.Status !== 'Active') {
            return false;
        }

        // Check expiration
        if (this.ExpiresAt && new Date(this.ExpiresAt) < new Date()) {
            return false;
        }

        return true;
    }

    // --- Status Transitions ---

    Revoke(): void {
        this.Status = 'Revoked';
    }

    Activate(): void {
        if (this.Status === 'Revoked') {
            throw new Error('Cannot activate a revoked API key');
        }
        this.Status = 'Active';
    }

    /**
     * Records the last use timestamp for this API key.
     * Should be called when the key is successfully used for authentication.
     *
     * @returns Promise<boolean> true if save succeeded
     */
    public async RecordUsage(): Promise<boolean> {
        this.LastUsedAt = new Date();
        return await this.Save();
    }
}

/**
 * Export function to ensure the class registration happens when this module is loaded
 */
export function LoadAPIKeyEntityExtended(): void {
    // This function doesn't need to do anything - it just ensures the module is loaded
    // and the @RegisterClass decorator is executed
}
